import { motion } from "framer-motion";
import { Facebook, Instagram } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

const SOCIALS = [
  {
    key: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/share/1HU9jwUkq9/?mibextid=wwXIfr",
    Icon: Facebook,
  },
  {
    key: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/trilogyearthworks",
    Icon: Instagram,
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      id="contact"
      className="relative bg-[#050505] border-t border-white/10 pt-24 pb-10 px-5 md:px-10 overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 bg-[color:var(--te-primary)] rotate-45" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[color:var(--te-primary)] font-display">
                Trilogy Earthworks
              </span>
            </div>
            <h3 className="font-display font-black uppercase text-white text-3xl md:text-4xl tracking-tighter leading-[0.95]">
              Central Queensland <br />
              <span className="font-serif-italic tracking-normal text-white/70">earthmoving crew.</span>
            </h3>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/50 font-display mb-4">
              Yard
            </div>
            <address className="not-italic text-white/80 font-body text-sm leading-relaxed">
              Rockhampton, QLD 4700<br />
              Australia
            </address>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.28em] text-white/50 font-display mb-4">
              Contact
            </div>
            <ul className="space-y-2 text-white/80 font-body text-sm">
              <li>
                <a href="tel:+61417984679" className="hover:text-[color:var(--te-primary)] transition-colors" data-testid="footer-phone">
                  0417 984 679
                </a>
              </li>
              <li>
                <a href="mailto:Trilogyearthworks@gmail.com" className="hover:text-[color:var(--te-primary)] transition-colors" data-testid="footer-email">
                  Trilogyearthworks@gmail.com
                </a>
              </li>
              <li className="text-white/60 text-xs uppercase tracking-[0.2em] pt-3">
                Mon–Sat · 06:30–17:30
              </li>
            </ul>
          </div>
        </div>

        {/* Large brand logo (grey TRILOGY wordmark removed per request) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-20 md:mt-28 select-none flex flex-col items-center"
        >
          <span
            className="inline-flex items-center justify-center w-full"
            data-testid="footer-logo-large"
          >
            <img
              src={LOGO_URL}
              alt="Trilogy Earthworks"
              className="w-full max-w-[1200px] h-auto object-contain block"
              draggable={false}
            />
          </span>
          <span className="mt-6 md:mt-8 text-[10px] md:text-xs uppercase tracking-[0.32em] text-white/40 font-display">
            Est. Rockhampton · QLD
          </span>
        </motion.div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-6 text-xs text-white/40 font-body">
          <div data-testid="footer-copy">© {year} Trilogy Earthworks. All rights reserved.</div>

          <div className="flex items-center gap-3" data-testid="footer-socials">
            {SOCIALS.map(({ key, label, href, Icon }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${key}`}
                className="w-10 h-10 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-[color:var(--te-primary)] hover:border-[color:var(--te-primary)] transition-colors duration-300"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>

          <div className="uppercase tracking-[0.24em]">
            Built with intent · Rockhampton
          </div>
        </div>
      </div>
    </footer>
  );
}
